import {forwardRef, useCallback, useImperativeHandle, useRef, useState} from "react";
import className from "classnames/bind";
import {FontAwesomeIcon, Loading} from "~/components";
import style from "../style/SeoCheck.module.scss";

const cn = className.bind(style);

// Màu badge theo cấp heading (đồng bộ với CriteriaItem).
const HEADING_COLORS = {
	1: {color: '#d4380d', bg: '#fff2e8'},
	2: {color: '#096dd9', bg: '#e6f4ff'},
	3: {color: '#389e0d', bg: '#f6ffed'},
	4: {color: '#531dab', bg: '#f9f0ff'},
	5: {color: '#c41d7f', bg: '#fff0f6'},
	6: {color: '#08979c', bg: '#e6fffb'},
};

function fileName(src) {
	try {
		return new URL(src).pathname.split('/').filter(Boolean).pop() || src;
	} catch {
		return src;
	}
}

/**
 * Khung xem trước giao diện website (iframe). Backend phục vụ HTML đã xử lý qua
 * `api/seo/preview/{id}` (chèn <base>, gỡ script gốc, kèm script highlight).
 *
 * - Expose `highlight({text|src, selector})` qua ref để cột tiêu chí điều khiển.
 * - Khi mở rộng toàn màn hình, hiện thêm panel điều hướng (heading + ảnh thiếu alt)
 *   để vẫn click highlight được ngay trong chế độ phóng to.
 */
const WebsitePreview = forwardRef(function WebsitePreview({reportId, previewToken, url, headings = [], images = [], expanded = false, onToggleExpand}, ref) {

	const iframeRef = useRef(null);
	const [loaded, setLoaded] = useState(false);

	const post = useCallback((payload) => {
		const win = iframeRef.current?.contentWindow;
		if (win) win.postMessage({type: 'maxseo-highlight', ...payload}, '*');
	}, []);

	useImperativeHandle(ref, () => ({highlight: post}), [post]);

	// Preview yêu cầu token ký (chống IDOR) — không có token thì không render iframe.
	if (!reportId || !previewToken) return null;

	const base = (process.env.REACT_APP_SERVICE_URL || '/api').replace(/\/$/, '');
	const src = `${base}/seo/preview/${reportId}?t=${encodeURIComponent(previewToken)}`;

	return (
		<div className={cn('preview', {expanded})}>
			<div className={cn('preview-head')}>
				<span><FontAwesomeIcon icon="fa-light fa-desktop" /> Xem giao diện website</span>
				<span className={cn('preview-actions')}>
					{url && (
						<a href={url} target="_blank" rel="noreferrer" className={cn('preview-open')}>
							Mở thật <FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />
						</a>
					)}
					<button
						type="button"
						className={cn('preview-expand')}
						onClick={onToggleExpand}
						title={expanded ? 'Thu nhỏ' : 'Mở rộng'}
					>
						<FontAwesomeIcon icon={expanded ? 'fa-light fa-compress' : 'fa-light fa-expand'} />
					</button>
				</span>
			</div>

			<div className={cn('preview-body')}>
				{/* Panel điều hướng — chỉ hiện khi mở rộng */}
				{expanded && (
					<div className={cn('preview-nav')}>
						{headings.length > 0 && (
							<div className={cn('preview-nav-block')}>
								<div className={cn('preview-nav-title')}>Heading</div>
								{headings.map((h, i) => {
									const c = HEADING_COLORS[h.level] || HEADING_COLORS[6];
									return (
										<div
											key={i}
											className={cn('hd-row', 'clickable')}
											style={{paddingLeft: `${(h.level - 1) * 14}px`}}
											onClick={() => post({text: h.text, selector: `h${h.level}`})}
										>
											<span className={cn('hd-badge')} style={{color: c.color, background: c.bg}}>H{h.level}</span>
											<span className={cn('hd-text')}>{h.text}</span>
										</div>
									);
								})}
							</div>
						)}

						{images.length > 0 && (
							<div className={cn('preview-nav-block')}>
								<div className={cn('preview-nav-title')}>Ảnh thiếu alt ({images.length})</div>
								<div className={cn('preview-nav-images')}>
									{images.map((s, i) => (
										<button key={i} type="button" className={cn('preview-nav-img')} title={s} onClick={() => post({selector: 'img', src: s})}>
											<img src={s} alt="" loading="lazy" />
											<span>{fileName(s)}</span>
										</button>
									))}
								</div>
							</div>
						)}
					</div>
				)}

				<div className={cn('preview-frame')}>
					{!loaded && <div className={cn('preview-loading')}><Loading noFixed /></div>}
					<iframe
						ref={iframeRef}
						src={src}
						title="Xem trước website"
						sandbox="allow-scripts allow-popups"
						onLoad={() => setLoaded(true)}
					/>
				</div>
			</div>

			<p className={cn('preview-hint')}>
				<FontAwesomeIcon icon="fa-light fa-circle-info" /> Bấm heading hoặc ảnh để nhảy tới vị trí tương ứng trên trang.
			</p>
		</div>
	);
});

export default WebsitePreview;
